import React, { useMemo, useState, useEffect, useCallback } from "react";
import Layout from "../Components/Layout";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Paper,
  TextField,
  Autocomplete,
  Button,
  Popover,
} from "@mui/material";
import { DateRange } from 'react-date-range';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import {
  BarChart as BarChartIcon,
  Phone as PhoneIcon,
  Token,
  Timer,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Message,
  Call as CallIcon,
  Chat as ChatIcon,
  ChatBubble as ChatBubbleIcon,
  ChatBubbleRounded as ChatBubbleRoundedIcon,
  ChatSharp as ChatSharpIcon,
} from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { FONT_FAMILY } from "../Constant";
import { getWebhookData } from "../redux/actions/billingActions";
import { getUsersListForDropdown } from "../redux/actions/userActions";
import useCallOnce from "../hooks/useCallOnce";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

function Dashboard() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { webhookData, loading, error } = useSelector((state) => state.billing);
  const { dropdown } = useSelector((state) => state.users);
  
  // User filter state
  const [userInput, setUserInput] = useState('');
  const [selectedUserOption, setSelectedUserOption] = useState(null);
  const [debounceTimer, setDebounceTimer] = useState(null);
  
  // Date range state - Initialize with empty dates
  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: 'selection'
    }
  ]);
  const [dateRangeAnchor, setDateRangeAnchor] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState('Select Date Range');

  // Fetch webhook data using useCallOnce - only once
  useCallOnce("billing/getWebhookData", () => {
    dispatch(getWebhookData());
  });

  useCallOnce("home/users/dropdown", () => {
    dispatch(getUsersListForDropdown('', 1, 20));
  }, [dispatch]);

  // Debounced search for user dropdown
  const debouncedSearch = useCallback((query) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    const timer = setTimeout(() => {
      if (query.length >= 2) {
        dispatch(getUsersListForDropdown(query, 1, 20));
      }
    }, 300);
    
    setDebounceTimer(timer);
  }, [debounceTimer, dispatch]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  // Handle search button click
  const handleSearch = () => {
    const userId = selectedUserOption ? (selectedUserOption._id || selectedUserOption.userId || selectedUserOption.id) : '';
    
    let startDate = '';
    let endDate = '';
    
    if (dateRange[0]?.startDate) {
      // Create a new date to avoid timezone issues
      const start = new Date(dateRange[0].startDate);
      startDate = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
      console.log('Original start date:', dateRange[0].startDate);
      console.log('Processed start date:', startDate);
    }
    
    if (dateRange[0]?.endDate) {
      // Create a new date to avoid timezone issues
      const end = new Date(dateRange[0].endDate);
      endDate = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
      console.log('Original end date:', dateRange[0].endDate);
      console.log('Processed end date:', endDate);
    }
    
    dispatch(getWebhookData({ 
      userId,
      startDate,
      endDate
    }));
  };

  // Handle reset
  const handleReset = () => {
    setUserInput('');
    setSelectedUserOption(null);
    setDateRange([{
      startDate: null,
      endDate: null,
      key: 'selection'
    }]);
    setSelectedDateRange('Select Date Range');
    dispatch(getWebhookData({}));
  };

  // Handle date range picker
  const handleDateRangeClick = (event) => {
    setDateRangeAnchor(event.currentTarget);
  };

  const handleDateRangeClose = () => {
    setDateRangeAnchor(null);
  };

  // Predefined date ranges
  const predefinedRanges = [
    { label: 'Today', getValue: () => ({ startDate: new Date(), endDate: new Date() }) },
    { label: 'Yesterday', getValue: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return { startDate: yesterday, endDate: yesterday };
    }},
    { label: 'This Week', getValue: () => {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      return { startDate: startOfWeek, endDate: today };
    }},
    { label: 'Last Week', getValue: () => {
      const today = new Date();
      const lastWeekEnd = new Date(today);
      lastWeekEnd.setDate(today.getDate() - today.getDay() - 1);
      const lastWeekStart = new Date(lastWeekEnd);
      lastWeekStart.setDate(lastWeekEnd.getDate() - 6);
      return { startDate: lastWeekStart, endDate: lastWeekEnd };
    }},
    { label: 'This Month', getValue: () => {
      const today = new Date();
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      return { startDate: startOfMonth, endDate: today };
    }},
    { label: 'Last Month', getValue: () => {
      const today = new Date();
      const startOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return { startDate: startOfLastMonth, endDate: endOfLastMonth };
    }},
    { label: 'This Year', getValue: () => {
      const today = new Date();
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      return { startDate: startOfYear, endDate: today };
    }}
  ];

  const handlePredefinedRange = (range) => {
    const { startDate, endDate } = range.getValue();
    setDateRange([{ startDate, endDate, key: 'selection' }]);
    setSelectedDateRange(range.label);
    handleDateRangeClose();
  };

  // Function to get formatted date
  const getFormattedDate = () => {
    const now = new Date();
    return now.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Function to get greeting based on current time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) {
       return "Good morning";
    } else if (hour >= 12 && hour < 17) {
      return "Good afternoon";
    } else if (hour >= 17 && hour < 21) {
      return "Good evening";
    } else {
      return "Good night";
    }
  };

  // Calculate webhook data summary
  const getWebhookSummary = () => {
    if (!webhookData || !webhookData.data || !Array.isArray(webhookData.data)) {
      return {
        totalWebhooks: 0,
        totalCost: 0,
        totalDuration: 0,
        percredit: 0,
        agentZenaPercentage: 0,
      };
    }

    const webhookArray = webhookData.data;

    const summary = {
      totalWebhooks: webhookArray.length,
      totalCost: 0,
      totalDuration: 0,
      percredit: webhookData?.getCosting[0]?.percredit || 0,
      agentZenaPercentage: webhookData?.getCosting[0]?.agentzenapercent || 0,
    };

    webhookArray.forEach((webhook) => {
      // Cost calculation
      if (webhook.price) {
        summary.totalCost += webhook.price;
      }

      // Duration calculation
      if (webhook.body?.data?.metadata?.call_duration_secs) {
        summary.totalDuration += webhook.body.data.metadata.call_duration_secs;
      }
    });

    return summary;
  };

  // Prepare chart data for last 30 days
  const chartData = useMemo(() => {
    if (!webhookData || !webhookData.data || !Array.isArray(webhookData.data)) {
      return [];
    }

    const webhookArray = webhookData.data;
    const dataMap = new Map();

    webhookArray.forEach((webhook) => {
      if (webhook.price) {
        const date = new Date(webhook.created_at);
        const key = `${date.getFullYear()}-${String(
          date.getMonth() + 1
        ).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

        if (dataMap.has(key)) {
          dataMap.set(key, dataMap.get(key) + webhook.price);
        } else {
          dataMap.set(key, webhook.price);
        }
      }
    });

    // Generate last 30 days with data or 0
    const currentDate = new Date();
    const last30Days = [];

    // Generate array of last 30 days
    for (let i = 29; i >= 0; i--) {
      const date = new Date(currentDate);
      date.setDate(currentDate.getDate() - i);

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      const dateKey = `${year}-${month}-${day}`;
      const displayDate = date.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
      });

      last30Days.push({
        date: displayDate,
        originalDate: dateKey,
        price: dataMap.has(dateKey)
          ? Math.round(dataMap.get(dateKey) * 100) / 100
          : 0,
      });
    }

    return last30Days;
  }, [webhookData]);

  const webhookSummary = getWebhookSummary();

  // Calculate total cost in dollars (same as BillingUsagePlans)
  // const totalCreditsCost = webhookSummary.totalCost * webhookSummary.percredit;
  // const totalZenaCost = (webhookSummary.agentZenaPercentage / 100) * totalCreditsCost;
  // const totalAmount = totalCreditsCost + totalZenaCost;

  // Format duration to rounded minutes with 'min' suffix
  const formatDuration = (seconds) => {
    if (!seconds) return "0 min";
    const minutes = Math.round(seconds / 60);
    return `${minutes} min`;
  };

  // Metric cards data with real data from webhook
  // const metrics = [
  //   {
  //     label: 'Conversations',
  //     value: webhookSummary.totalWebhooks.toLocaleString(),
  //     icon: PhoneIcon,
  //     color: '#10b981'
  //   },
  //   {
  //     label: 'Total Credits',
  //     value: webhookSummary.totalCost.toLocaleString(),
  //     icon: Token,
  //     color: '#f59e0b'
  //   },
  //   {
  //     label: 'Total Duration',
  //     value: formatDuration(webhookSummary.totalDuration),
  //     icon: Timer,
  //     color: '#8b5cf6'
  //   },
  //   {
  //     label: 'Total Cost',
  //     value: `$${totalAmount.toFixed(4)}`,
  //     icon: MoneyIcon,
  //     color: '#ef4444'
  //   }
  // ];

  const metrics = [
    {
      label: "Messages",
      value: (webhookData?.processedData?.results[0]?.total_messages || 0).toLocaleString(),
      // perValue: `$${(webhookData?.processedData?.perMessage || 0).toFixed(4)}`,
      // perValue: `$${(
      //   ((webhookData?.processedData?.totalCharges_llm ?? 0) +
      //     (webhookData?.getCosting?.[0]?.percredit ?? 0) *
      //       (webhookData?.processedData?.results[0]?.total_call_credits ?? 0)) /
      //   webhookData?.processedData?.results[0]?.total_messages ?? 0
      // ).toFixed(4)}`,
      perValue: `$${((
        (webhookData?.getCosting?.[0]?.percredit || 0) *
        (webhookData?.processedData?.results[0]?.total_call_credits || 0)
      ) / (webhookData?.processedData?.results[0]?.total_messages || 1)).toFixed(4)}`,
      perLabel: "Average cost per message",
      icon: Message,
      color: "#10b981",
    },
    {
      label: "Minutes",
      value: formatDuration(
        webhookData?.processedData?.results[0]?.total_call_duration_secs
      ),
      // perValue: `$${(webhookData?.processedData?.perMinuteDollars || 0).toFixed(
      //   4
      // )}`,
      // perValue: `$${(
      //   ((webhookData?.processedData?.totalCharges_llm ?? 0) +
      //     (webhookData?.getCosting?.[0]?.percredit ?? 0) *
      //       (webhookData?.processedData?.results[0]?.total_call_credits ?? 0)) /
      //   ((webhookData?.processedData?.results[0]?.total_call_duration_secs ??
      //     1) /
      //     60)
      // ) // convert secs → mins
      //   .toFixed(4)}`,
      perValue: `$${((webhookData?.processedData?.totalCharges_llm || 0) / ((webhookData?.processedData?.results[0]?.total_call_duration_secs || 0) / 60 || 1)).toFixed(3)}`,
      perLabel: "Average cost per minute",
      icon: Timer,
      color: "#10b981",
    },
    {
      label: "Calls",
      value: (webhookData?.processedData?.results[0]?.total_conversation || 0).toLocaleString(),
      // perValue: `$${(webhookData?.processedData?.results[0]?.total_conversation / webhookData?.processedData?.totalCharges_llm + (webhookData?.getCosting?.[0]?.percredit * webhookData?.processedData?.results[0]?.total_call_credits) ).toFixed(4)}`,
      perValue: `$${(
        ((webhookData?.processedData?.totalCharges_llm ?? 0) +
          (webhookData?.getCosting?.[0]?.percredit ?? 0) *
            (webhookData?.processedData?.results[0]?.total_call_credits ?? 0)) /
        (webhookData?.processedData?.results[0]?.total_conversation || 1)
      ).toFixed(4)}`,
      perLabel: "Average cost per call",
      icon: PhoneIcon,
      color: "#10b981",
    },
    {
      label: "Chat Credits",
      value: (webhookData?.processedData?.results[0]?.total_call_credits || 0).toLocaleString(),
      // perValue: `$${(webhookData?.processedData?.perMinuteDollars || 0).toFixed(4)}`,
      perValue: null,
      perLabel: null,
      icon: ChatSharpIcon,
      color: "#555",
    },
    {
      label: "LLM Credit",
      value: (webhookData?.processedData?.results[0]?.lmmCredits || 0).toLocaleString(),
      perValue: null,
      perLabel: null,
      icon: Token,
      color: "#555",
    },
    {
      label: "Total Credits",
      value: (
        (webhookData?.processedData?.results[0]?.lmmCredits || 0) +
        (webhookData?.processedData?.results[0]?.total_call_credits || 0)
      ).toLocaleString(),
      perValue: Math.round(((
        (webhookData?.processedData?.results[0]?.lmmCredits || 0) +
        (webhookData?.processedData?.results[0]?.total_call_credits || 0)
      ) / (webhookData?.processedData?.results[0]?.total_conversation || 1)),
      4) + " Credits/Call",
      perLabel: "Average Cost",
      icon: Token,
      color: "#10b981",
    },
    {
      label: "Chat Cost",
      value: `$${(
        (webhookData?.getCosting?.[0]?.percredit || 0) *
        (webhookData?.processedData?.results[0]?.total_call_credits || 0)
      ).toFixed(2)}`,
      perValue: null,
      perLabel: null,
      icon: MoneyIcon,
      color: "#ef4444",
    },
    {
      label: "LLM Cost",
      value: `$${(webhookData?.processedData?.totalCharges_llm || 0).toFixed(
        2
      )}`,
      perValue: null,
      perLabel: null,
      icon: MoneyIcon,
      color: "#ef4444",
    },
    {
      label: "Total Cost",
      value: `$${(
        (webhookData?.processedData?.totalCharges_llm || 0) +
        (webhookData?.getCosting?.[0]?.percredit || 0) *
          (webhookData?.processedData?.results[0]?.total_call_credits || 0)
      ).toFixed(2)}`,
      perValue: null,
      perLabel: null,
      icon: MoneyIcon,
      color: "#ef4444",
    },
  ];

  return (
    <Layout>
      <Box sx={{ mt: { xs: 0, sm: 0 }, px: { xs: 0, sm: 0 } }}>
        {/* Header Section */}
        <Box sx={{ mb: { xs: 3, sm: 4 }, px: { xs: 1, sm: 0 } }}>
          <Typography
            variant="body2"
            sx={{
              color: "#9ca3af",
              fontSize: { xs: "12px", sm: "14px" },
              mb: { xs: 0.25, sm: 0.5 },
              fontFamily: FONT_FAMILY,
            }}
          >
            My Workspace
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "#111827",
              fontWeight: 600,
              fontSize: { xs: "12px", sm: "14px", md: "16px" },
              mt: { xs: 0.25, sm: 0.5, md: 1 },
              mb: { xs: 0.5, sm: 1, md: 1.5 },
              fontFamily: FONT_FAMILY,
            }}
          >
            {getFormattedDate()}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#111827",
              fontSize: { xs: "1.5rem", sm: "1.5rem", md: "1.5rem" },
              fontFamily: FONT_FAMILY,
            }}
          >
            {getGreeting()}, {user?.company || "Admin"}
          </Typography>
        </Box>

         {/* User Filter Section */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Date Range Picker */}
            <Grid size={{ xs: 12, xl: 2, lg: 3, md: 4 }}>
              <Button
                variant="outlined"
                onClick={handleDateRangeClick}
                startIcon={<CalendarIcon />}
                size="medium"
                fullWidth
                sx={{
                  borderColor: '#e2e8f0',
                  color: '#6b7280',
                  backgroundColor: '#f8fafc',
                  textTransform: 'none',
                  fontWeight: 500,
                  justifyContent: 'space-between',
                  borderRadius: '8px',
                  '&:hover': { 
                    borderColor: '#cbd5e1',
                    backgroundColor: '#e2e8f0',
                    color: '#374151'
                  },
                }}
              >
                {selectedDateRange}
              </Button>
              
              <Popover
                open={Boolean(dateRangeAnchor)}
                anchorEl={dateRangeAnchor}
                onClose={handleDateRangeClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                PaperProps={{
                  sx: { 
                    p: 3,
                    minWidth: 600,
                    maxWidth: 800,
                    backgroundColor: '#fafbfc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                  }
                }}
              >
                <Box sx={{ display: 'flex', gap: 3, mb: 2 }}>
                  {/* Predefined Ranges */}
                  <Box sx={{ minWidth: 150 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: '#6b7280' }}>
                      Quick Select
                    </Typography>
                    {predefinedRanges.map((range) => (
                      <Button
                        key={range.label}
                        onClick={() => handlePredefinedRange(range)}
                        size="small"
                        sx={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          color: '#6b7280',
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          mb: 0.5,
                          '&:hover': {
                            backgroundColor: '#e2e8f0',
                            color: '#374151',
                            borderColor: '#cbd5e1',
                          }
                        }}
                      >
                        {range.label}
                      </Button>
                    ))}
                  </Box>
                  
                  {/* Calendar */}
                  <Box sx={{
                    '& .rdrDefinedRangesWrapper': {
                      display: 'none'
                    },
                    '& .rdrDateRangePickerWrapper': {
                      backgroundColor: 'transparent'
                    },
                    '& .rdrCalendarWrapper': {
                      backgroundColor: 'transparent',
                      borderRadius: '8px'
                    },
                    '& .rdrMonthAndYearWrapper': {
                      backgroundColor: '#f8fafc',
                      borderBottom: '1px solid #e2e8f0'
                    },
                    '& .rdrMonthAndYearPickers select': {
                      backgroundColor: '#f8fafc',
                      color: '#374151',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px'
                    },
                    '& .rdrWeekDays': {
                      backgroundColor: '#f8fafc'
                    },
                    '& .rdrDay': {
                      color: '#374151'
                    },
                    '& .rdrDay:hover': {
                      backgroundColor: '#e2e8f0'
                    },
                    '& .rdrDayNumber span': {
                      color: '#374151'
                    },
                    '& .rdrDayInRange': {
                      backgroundColor: '#f3f4f6 !important',
                      color: '#111827 !important'
                    },
                    '& .rdrDayStartOfRange, .rdrDayEndOfRange': {
                      backgroundColor: '#111827 !important',
                      color: 'white !important'
                    },
                    '& .rdrDayStartOfRange .rdrDayNumber span, .rdrDayEndOfRange .rdrDayNumber span': {
                      color: 'white !important'
                    },
                    '& .rdrDaySelected': {
                      backgroundColor: '#111827 !important',
                      color: 'white !important'
                    },
                    '& .rdrDaySelected .rdrDayNumber span': {
                      color: 'white !important'
                    },
                    '& .rdrDaySelected:hover': {
                      backgroundColor: '#0b1220 !important'
                    },
                    // Override react-date-range default styles
                    '& .rdrDayInRange .rdrDayNumber span': {
                      color: '#111827 !important'
                    },
                    '& .rdrDayStartOfRange .rdrDayNumber, .rdrDayEndOfRange .rdrDayNumber': {
                      color: 'white !important'
                    },
                    '& .rdrDayInRange .rdrDayNumber': {
                      color: '#111827 !important'
                    }
                  }}>
                    <DateRange
                      editableDateInputs={true}
                      onChange={(item) => {
                        if (item && item.selection) {
                          setDateRange([item.selection]);
                        }
                      }}
                      moveRangeOnFirstSelection={false}
                      ranges={dateRange}
                      showDateDisplay={false}
                      months={1}
                      direction="horizontal"
                    />
                  </Box>
                </Box>
                
                {/* Save and Cancel Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: 1, 
                  pt: 2,
                  borderTop: '1px solid #e2e8f0'
                }}>
                  <Button
                    variant="outlined"
                    onClick={handleDateRangeClose}
                    size="small"
                    sx={{
                      borderColor: '#d1d5db',
                      color: '#6b7280',
                      textTransform: 'none',
                      '&:hover': {
                        borderColor: '#9ca3af',
                        backgroundColor: '#f9fafb'
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => {
                      // Update the selected date range text based on selection
                      if (dateRange[0]?.startDate && dateRange[0]?.endDate) {
                        const startDate = new Date(dateRange[0].startDate);
                        const endDate = new Date(dateRange[0].endDate);
                        const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                        const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                        setSelectedDateRange(`${startStr} - ${endStr}`);
                      } else if (dateRange[0]?.startDate) {
                        const startDate = new Date(dateRange[0].startDate);
                        const startStr = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                        setSelectedDateRange(`${startStr} - Select End Date`);
                      } else {
                        setSelectedDateRange('Select Date Range');
                      }
                      handleDateRangeClose();
                    }}
                    size="small"
                    sx={{
                      backgroundColor: '#111827',
                      color: 'white',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: '#0b1220'
                      }
                    }}
                  >
                    Save
                  </Button>
                </Box>
              </Popover>
            </Grid>

            <Grid size={{ xs: 12, lg: 3, md: 4 }}>
              <Autocomplete
                size="small"
                fullWidth
                options={(dropdown.users || [])}
                loading={Boolean(dropdown?.loading)}
                loadingText="Loading..."
                value={selectedUserOption}
                getOptionLabel={(u) => (u?.email || u?.name || u?.company || u?._id || u?.userId || u?.id || '')}
                isOptionEqualToValue={(a, b) => (a?._id || a?.userId || a?.id) === (b?._id || b?.userId || b?.id)}
                onChange={(e, value) => {
                  setSelectedUserOption(value || null);
                }}
                inputValue={userInput}
                onInputChange={(e, value, reason) => {
                  setUserInput(value);
                  // Only trigger API on user typing, not on option select/clear
                  if (reason === 'input') {
                    debouncedSearch(value || '');
                  }
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Select Brand"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {dropdown?.loading ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            </Grid>
            
            <Grid display="flex" gap={1} size={{ xs: 12, lg: 3, md: 4 }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                size="medium"
                fullWidth
                sx={{
                  bgcolor: '#111827',
                  '&:hover': { bgcolor: '#0b1220' },
                  textTransform: 'none',
                }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                size="medium"
                fullWidth
                sx={{
                  borderColor: '#111827',
                  color: '#111827',
                  textTransform: 'none',
                  '&:hover': { borderColor: '#0b1220', backgroundColor: '#f9fafb' },
                }}
              >
                Reset
              </Button>
            </Grid>
            
            
          </Grid>
        </Paper>

        {/* Metric Cards Section */}
        <Box sx={{ mb: { xs: 3, sm: 4 }, px: { xs: 1, sm: 0 } }}>
          <Box
            sx={{
              overflowX: "auto",
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f3f4f6",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#d1d5db",
                borderRadius: "3px",
              },
            }}
          >
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              {metrics.map((metric, index) => {
                const IconComponent = metric.icon;
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                    <Card
                      sx={{
                        height: "100%",
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                        boxShadow: "none",
                        backgroundColor: "#ffffff",
                        "&:hover": {
                          borderColor: "#d1d5db",
                          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                        },
                      }}
                    >
                      <CardContent
                        sx={{ p: { xs: 1.5, sm: 2 }, position: "relative" }}
                      >
                        {/* Icon */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            mb: 1,
                          }}
                        >
                          <IconComponent
                            sx={{
                              fontSize: { xs: 24, sm: 28 },
                              color: metric.color,
                            }}
                          />
                        </Box>

                        {/* Metric Label */}
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#6b7280",
                            fontSize: { xs: "11px", sm: "12px", md: "13px" },
                            mb: { xs: 0.75, sm: 1 },
                            fontWeight: 500,
                            fontFamily: FONT_FAMILY,
                            textAlign: "center",
                          }}
                        >
                          {metric.label}
                        </Typography>

                        {/* Metric Value */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {loading ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: {
                                  xs: "1.5rem",
                                  sm: "1.75rem",
                                  md: "2.25rem",
                                },
                              }}
                            >
                              <CircularProgress
                                size={20}
                                sx={{ color: metric.color }}
                              />
                            </Box>
                          ) : (
                            <Typography
                              variant="h5"
                              sx={{
                                fontWeight: 700,
                                color: "#111827",
                                fontSize: {
                                  xs: "1.1rem",
                                  sm: "1.25rem",
                                  md: "1.5rem",
                                },
                                fontFamily: FONT_FAMILY,
                              }}
                            >
                              {metric.value}
                            </Typography>
                          )}

                          {/* Per Value */}
                          {metric.perValue && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 0.25,
                              }}
                            >
                              {loading ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minHeight: "1rem",
                                  }}
                                >
                                  <CircularProgress
                                    size={12}
                                    sx={{ color: metric.color }}
                                  />
                                </Box>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    color: metric.color,
                                    fontSize: {
                                      xs: "10px",
                                      sm: "11px",
                                      md: "12px",
                                    },
                                    fontFamily: FONT_FAMILY,
                                  }}
                                >
                                  {metric.perValue}
                                </Typography>
                              )}
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#9ca3af",
                                  fontSize: { xs: "9px", sm: "10px" },
                                  fontFamily: FONT_FAMILY,
                                  textAlign: "center",
                                }}
                              >
                                {metric.perLabel}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>

        {/* Main Content Area */}
        {/* <Card 
          sx={{ 
            border: '1px solid #e5e7eb',
            borderRadius: 2,
            boxShadow: 'none',
            backgroundColor: '#ffffff',
            minHeight: { xs: 300, sm: 400 },
            mx: { xs: 1, sm: 0 }
          }}
        >
          <CardContent sx={{ 
            py: { xs: 3, sm: 4 },
            px: { xs: 2, sm: 4 }
          }}>
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: { xs: 6, sm: 8 }
              }}>
                <CircularProgress size={40} sx={{ color: '#111827' }} />
              </Box>
            ) : error ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: { xs: 6, sm: 8 }
              }}>
                <BarChartIcon sx={{ 
                  fontSize: { xs: 48, sm: 56, md: 64 }, 
                  color: '#ef4444', 
                  mb: { xs: 1.5, sm: 2 } 
                }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    color: '#ef4444',
                    mb: { xs: 0.75, sm: 1 },
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    fontFamily: FONT_FAMILY
                  }}
                >
                  Error loading data
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#9ca3af',
                    textAlign: 'center',
                    maxWidth: { xs: 300, sm: 400 },
                    fontSize: { xs: '12px', sm: '14px' },
                    fontFamily: FONT_FAMILY
                  }}
                >
                  {error}
                </Typography>
              </Box>
            ) : webhookSummary.totalWebhooks > 0 ? (
              <>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 2, sm: 0 }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUpIcon sx={{ color: '#10b981', fontSize: 24 }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        color: '#111827',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' },
                        fontFamily: FONT_FAMILY
                      }}
                    >
                                             Last 30 Days Analysis
                    </Typography>
                  </Box>
                  
                  
                </Box>

                {chartData.length > 0 ? (
                  <Box sx={{ height: { xs: 250, sm: 300, md: 350 } }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
                         <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                         <XAxis 
                           dataKey="date" 
                           stroke="#6b7280"
                           fontSize={10}
                           tick={{ fill: '#6b7280' }}
                           angle={-45}
                           textAnchor="end"
                           height={70}
                           interval={0}
                         />
                                                 <YAxis 
                           stroke="#6b7280"
                           fontSize={12}
                           tick={{ fill: '#6b7280' }}
                           tickFormatter={(value) => `${value}`}
                         />
                         <Tooltip 
                           contentStyle={{
                             backgroundColor: '#ffffff',
                             border: '1px solid #e5e7eb',
                             borderRadius: '8px',
                             boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                           }}
                           labelStyle={{ color: '#111827', fontWeight: 600 }}
                           formatter={(value, name) => [`${value}`, 'Credit']}
                         />
                        <Line 
                          type="monotone" 
                          dataKey="price" 
                          stroke="#10b981" 
                          strokeWidth={3}
                          dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                          activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    py: { xs: 4, sm: 6 }
                  }}>
                    <BarChartIcon sx={{ 
                      fontSize: { xs: 48, sm: 56 }, 
                      color: '#9ca3af', 
                      mb: { xs: 1.5, sm: 2 } 
                    }} />
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: '#6b7280',
                        textAlign: 'center',
                        fontSize: { xs: '14px', sm: '16px' },
                        fontFamily: FONT_FAMILY
                      }}
                    >
                      No cost data available for the selected period
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: { xs: 6, sm: 8 }
              }}>
                <BarChartIcon sx={{ 
                  fontSize: { xs: 48, sm: 56, md: 64 }, 
                  color: '#9ca3af', 
                  mb: { xs: 1.5, sm: 2 } 
                }} />
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    color: '#6b7280',
                    mb: { xs: 0.75, sm: 1 },
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    fontFamily: FONT_FAMILY
                  }}
                >
                  No metrics
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#9ca3af',
                    textAlign: 'center',
                    maxWidth: { xs: 300, sm: 400 },
                    fontSize: { xs: '12px', sm: '14px' },
                    fontFamily: FONT_FAMILY
                  }}
                >
                  There are no metrics for the specified period.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card> */}
      </Box>
    </Layout>
  );
}

export default Dashboard;
